import {
  createTemplateQuestion,
  deleteTemplateQuestion,
  listTemplateQuestions,
  matchTemplateVariants,
  setVariantAnswers,
  updateTemplateQuestion,
  type ContractGenerationTemplateMatchAnswer,
  type ContractGenerationTemplateMatchResponse,
  type ContractGenerationTemplateQuestionCreateRequest,
  type ContractGenerationTemplateQuestionUpdateRequest,
  type ContractGenerationTemplateQuestionsResponse,
  type ContractGenerationVariantAnswerUpdate,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

/**
 * A template's questions — what it asks before it can produce a document
 * ("Gender", "Language", "Annual salary").
 *
 * Two halves meet here. A **variant** answers a question with a *comparison*:
 * the word it serves, or an operator and one or two operands ("is under
 * 50,000", "is between 50,000 and 100,000"). The person **drafting** answers
 * with a plain *value*. `match` puts the values against the comparisons and
 * returns every variant whose answer the value satisfies.
 *
 * Two kinds of silence, and both widen the result: a question a variant hasn't
 * answered doesn't constrain that variant, and a question the user leaves
 * blank isn't filtered on. A half-filled form returns more candidates, never
 * fewer — which is why `match` returns a list and the user picks from it.
 *
 * **Every write answers with the whole state** — questions, every variant's
 * answers, and the draftability verdict — because a write is exactly when
 * those change. Callers adopt the response; they never re-fetch to find out
 * what happened.
 */
export const TemplateQuestionsService = {
  async list(templateUid: string): Promise<ContractGenerationTemplateQuestionsResponse> {
    try {
      const response = await listTemplateQuestions({ path: { template_uid: templateUid } });
      if (!response.data) {
        throw new Error('Failed to load the template’s questions');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to load the template’s questions.'));
    }
  },

  /**
   * Add a question: a label, a hint and a type, and nothing else.
   *
   * A choice question's admissible values are **derived, not authored** — a
   * document answers with free text and the question's `values` is the distinct
   * set of what the documents have said, which is what a picker offers. So
   * there is nothing to state here, and no options route to state it on.
   *
   * It is appended last: questions are asked in the order they were created,
   * which is the only order there is — nothing carries an order index.
   *
   * `required_for_variants` defaults to false on purpose: adding a question
   * then costs a template nothing until its documents answer it.
   */
  async create(
    templateUid: string,
    payload: ContractGenerationTemplateQuestionCreateRequest
  ): Promise<ContractGenerationTemplateQuestionsResponse> {
    try {
      const response = await createTemplateQuestion({
        path: { template_uid: templateUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to add the question');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to add the question.'));
    }
  },

  /** Rename, reword the hint, or change whether every document must answer.
   *  The type is immutable — it decides the shape of every answer already given. */
  async update(
    templateUid: string,
    questionUid: string,
    payload: ContractGenerationTemplateQuestionUpdateRequest
  ): Promise<ContractGenerationTemplateQuestionsResponse> {
    try {
      const response = await updateTemplateQuestion({
        path: { template_uid: templateUid, question_uid: questionUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to update the question');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update the question.'));
    }
  },

  async remove(
    templateUid: string,
    questionUid: string
  ): Promise<ContractGenerationTemplateQuestionsResponse> {
    try {
      const response = await deleteTemplateQuestion({
        path: { template_uid: templateUid, question_uid: questionUid },
      });
      if (!response.data) {
        throw new Error('Failed to delete the question');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete the question.'));
    }
  },

  /**
   * One variant's COMPLETE answer set — this replaces, it does not merge. A
   * question left out of the list stops constraining that variant, which is
   * the only way to say "this document doesn't care about that question".
   */
  async setVariantAnswers(
    templateUid: string,
    variantUid: string,
    answers: ContractGenerationVariantAnswerUpdate[]
  ): Promise<ContractGenerationTemplateQuestionsResponse> {
    try {
      const response = await setVariantAnswers({
        path: { template_uid: templateUid, variant_uid: variantUid },
        body: { answers },
      });
      if (!response.data) {
        throw new Error('Failed to save the document’s answers');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to save the document’s answers.'));
    }
  },

  /**
   * Every variant consistent with the drafting user's answers.
   *
   * Matching narrows, it does not resolve: several matches is the normal state
   * of a half-filled form, and an empty list is a 200 — nothing about the
   * request was wrong, no document simply covers that combination.
   */
  async match(
    templateUid: string,
    answers: ContractGenerationTemplateMatchAnswer[]
  ): Promise<ContractGenerationTemplateMatchResponse> {
    try {
      const response = await matchTemplateVariants({
        path: { template_uid: templateUid },
        body: { answers },
      });
      if (!response.data) {
        throw new Error('Failed to match the answers');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to match the answers.'));
    }
  },
};
